import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import chateaLogo from "../../assets/chatea.png";
import { acceptReferralChange } from "../../api/referralChange";

const AcceptInvitation = () => {
  const location = useLocation();

  // Extraer token y code de la URL
  const urlParams = new URLSearchParams(location.search);
  const token = urlParams.get("token");

  // Eliminar el slash al final del código de referido
  const referralCode = urlParams.get("code")?.replace(/\/$/, "") || null;

  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [tokenExpired, setTokenExpired] = useState(false);
  const [invitationAccepted, setInvitationAccepted] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [tokenInfo, setTokenInfo] = useState(null);

  // Función para verificar si el token está expirado
  const checkTokenExpiry = (token) => {
    try {
      const tokenParts = token.split(".");
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const currentTime = Date.now() / 1000;
        const isExpired = payload.exp < currentTime;

        setTokenInfo({
          email: payload.email,
          purpose: payload.purpose,
          expiresAt: new Date(payload.exp * 1000),
          isExpired: isExpired,
        });

        return isExpired;
      }
    } catch (error) {
      console.error("Error al decodificar el token:", error);
      return true;
    }
    return true;
  };

  // Validar que tengamos los parámetros necesarios al cargar
  useEffect(() => {
    if (!token || !referralCode) {
      setError("NO_PARAMS");
      Swal.fire({
        title: "Error",
        text: "No se encontró el token de invitación o el código de referido en la URL.",
        icon: "error",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#ef4444",
      });
      return;
    }

    // Verificar si el token está expirado
    const expired = checkTokenExpiry(token);
    if (expired) {
      setTokenExpired(true);
      setError("TOKEN_EXPIRED");
    }
  }, [token, referralCode]);

  const handleAcceptInvitation = async () => {
    if (!token || !referralCode) {
      Swal.fire({
        title: "Error",
        text: "No se encontró el token de invitación o el código de referido.",
        icon: "error",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#ef4444",
      });
      return;
    }

    // Mostrar confirmación antes de proceder
    const confirmResult = await Swal.fire({
      title: "¿Confirmar cambio de comunidad?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, cambiar comunidad",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#009ee3",
      cancelButtonColor: "#6b7280",
      reverseButtons: true,
      focusCancel: true,
    });

    // Si el usuario cancela, no hacer nada
    if (!confirmResult.isConfirmed) {
      return;
    }

    try {
      setAccepting(true);
      const response = await acceptReferralChange(token, referralCode);

      await Swal.fire({
        title: "¡Invitación aceptada!",
        text:
          response.message ||
          "Has sido transferido exitosamente a la nueva comunidad",
        icon: "success",
        confirmButtonText: "Continuar",
        confirmButtonColor: "#009ee3",
        showClass: {
          popup: "animate__animated animate__fadeInDown",
        },
        hideClass: {
          popup: "animate__animated animate__fadeOutUp",
        },
      });

      // Cambiar el estado para mostrar la vista de éxito
      setInvitationAccepted(true);
    } catch (err) {
      console.error("Error al aceptar invitación:", err);

      let errorMessage =
        "Error al procesar la invitación. Inténtalo nuevamente.";
      let errorTitle = "Error";

      // Manejar diferentes tipos de error más específicamente
      if (err.message) {
        errorMessage = err.message;
      } else if (err.errors) {
        const errorKeys = Object.keys(err.errors);
        if (errorKeys.length > 0) {
          errorMessage = err.errors[errorKeys[0]][0];
        }
      } else if (err.detail) {
        // Error del servidor con detalle
        if (err.detail.includes("Value cannot be null")) {
          errorTitle = "Invitación inválida";
          errorMessage =
            "Los datos de la invitación están incompletos o son inválidos. Verifica que el enlace de invitación sea correcto.";
        } else {
          errorMessage =
            "Error interno del servidor. Contacta al administrador.";
        }
      } else if (err.title) {
        errorMessage = err.title;
      }

      // Si el token o código están vacíos
      if (!token || !referralCode) {
        errorTitle = "Invitación inválida";
        errorMessage =
          "El enlace de invitación no contiene los datos necesarios. Verifica que el enlace sea correcto y completo.";
      }

      // Verificar si es un error de token expirado del servidor
      if (
        err.detail &&
        err.detail.includes(
          "No se encontraron el correo o código referido en la base de datos"
        )
      ) {
        // Verificar si el token está expirado localmente también
        const expired = checkTokenExpiry(token);
        if (expired) {
          setTokenExpired(true);
          setError("TOKEN_EXPIRED");
          return;
        }
      }

      Swal.fire({
        title: errorTitle,
        text: errorMessage,
        icon: "error",
        confirmButtonText: "Entendido",
        confirmButtonColor: "#ef4444",
        footer:
          process.env.NODE_ENV === "development"
            ? `<small>Debug: ${JSON.stringify(err).substring(
                0,
                200
              )}...</small>`
            : undefined,
      });
    } finally {
      setAccepting(false);
    }
  };

  if (loading) {
    return (
      <div className="accept-invitation">
        <div className="accept-invitation__container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Procesando...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error === "NO_PARAMS") {
    return (
      <div className="accept-invitation">
        <div className="accept-invitation__container">
          <div className="error-state">
            <h1>Invitación no disponible</h1>
            <p>Esta invitación no es válida o faltan parámetros.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error === "TOKEN_EXPIRED" || tokenExpired) {
    return (
      <div className="accept-invitation">
        <figure className="accept-invitation__logo">
          <img src={chateaLogo} alt="ChateaPro" />
        </figure>
        <div className="accept-invitation__container">
          <div className="error-state">
            <h1
              className="accept-invitation__title"
              style={{ color: "#ef4444" }}
            >
              Invitación expirada
            </h1>

            <div
              className="accept-invitation__expired-notice"
              style={{
                backgroundColor: "#fef2f2",
                border: "1px solid #fecaca",
                borderRadius: "8px",
                padding: "16px",
                margin: "20px 0",
                width: "80%",
              }}
            >
              <p
                style={{
                  color: "#dc2626",
                  fontWeight: "500",
                  margin: "0 0 8px 0",
                }}
              >
                ⏰ Esta invitación ha expirado
              </p>
              <p style={{ color: "#7f1d1d", margin: "0" }}>
                La invitación para cambiar de comunidad ya no es válida.
              </p>
            </div>

            <div className="accept-invitation__actions">
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "14px",
                  marginBottom: "16px",
                }}
              >
                Para obtener una nueva invitación, contacta a la persona que te
                invitó o solicita que te envíen un nuevo enlace.
              </p>

              <button
                className="accept-invitation__button"
                onClick={() => window.close()}
                style={{
                  backgroundColor: "#6b7280",
                  cursor: "pointer",
                }}
              >
                Cerrar ventana
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Nueva vista para cuando la invitación ya fue aceptada
  if (invitationAccepted) {
    return (
      <div className="accept-invitation">
        <figure className="accept-invitation__logo">
          <img src={chateaLogo} alt="ChateaPro" />
        </figure>
        <div className="accept-invitation__container">
          <div className="error-state">
            <h1
              className="accept-invitation__title"
              style={{ color: "#10b981" }}
            >
              ¡Invitación aceptada exitosamente!
            </h1>

            <div
              className="accept-invitation__success-notice"
              style={{
                backgroundColor: "#f0fdf4",
                border: "1px solid #bbf7d0",
                borderRadius: "8px",
                padding: "16px",
                margin: "20px 0",
                width: "80%",
              }}
            >
              <p
                style={{
                  color: "#059669",
                  fontWeight: "500",
                  margin: "0 0 8px 0",
                }}
              >
                ✅ Cambio de comunidad completado
              </p>
              <p style={{ color: "#065f46", margin: "0" }}>
                Has sido transferido exitosamente a tu nueva comunidad con el
                código: <strong>{referralCode}</strong>
              </p>
            </div>

            <div className="accept-invitation__actions">
              <p
                style={{
                  color: "#6b7280",
                  fontSize: "14px",
                  marginBottom: "16px",
                  width: "100% !important;",
                }}
              >
                La invitación ya ha sido procesada. Puedes cerrar esta ventana
                de forma segura.
              </p>

              <button
                className="accept-invitation__button"
                onClick={() => window.close()}
                style={{
                  backgroundColor: "#10b981",
                  cursor: "pointer",
                  width: "100% !important;",
                }}
              >
                Cerrar ventana
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="accept-invitation">
      <figure className="accept-invitation__logo">
        <img src={chateaLogo} alt="ChateaPro" />
      </figure>
      <div className="accept-invitation__container">
        <h1 className="accept-invitation__title">
          Invitación cambio de comunidad
        </h1>

        <div className="accept-invitation__important-notice">
          <p>
            Al aceptar esta invitación, serás removido de tu comunidad actual y
            transferido a la nueva comunidad. Esta acción no se puede deshacer.
          </p>
        </div>

        <p className="accept-invitation__validity">
          Esta invitación es válida por tiempo limitado
        </p>

        <div className="accept-invitation__communities">
          <div className="community-box community-box--new">
            <div className="community-title">Nuevo código de referido</div>
            <div className="community-name">
              {referralCode ? referralCode : "Código no disponible"}
            </div>
          </div>
        </div>

        <button
          className="accept-invitation__button"
          onClick={handleAcceptInvitation}
          disabled={accepting || !token || !referralCode}
        >
          {accepting ? (
            <>
              <span className="loading-spinner"></span>
              Procesando...
            </>
          ) : (
            "Aceptar invitación"
          )}
        </button>

        <p className="accept-invitation__ignore">
          Si no deseas unirte a esta comunidad puedes ignorar esta invitación
        </p>
      </div>
    </div>
  );
};

export default AcceptInvitation;
